/**
 * Safe localStorage wrapper with quota handling
 * Prevents QuotaExceededError from crashing the app
 */

class SafeStorage {
  /**
   * Safely set an item in localStorage
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @returns {boolean} - Success status
   */
  setItem(key, value) {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ [STORAGE] localStorage quota exceeded, attempting cleanup...');
        
        // Strategy 1: Clear logs first (least important data)
        this.clearLogs();
        
        try {
          localStorage.setItem(key, value);
          console.log('✅ [STORAGE] Successfully saved after clearing logs');
          return true;
        } catch (error2) {
          console.error('❌ [STORAGE] Still quota exceeded after log cleanup');
          
          // Strategy 2: Clear cached gallery products
          this.clearCachedProducts();
          
          try {
            localStorage.setItem(key, value);
            console.log('✅ [STORAGE] Successfully saved after clearing products');
            return true;
          } catch (error3) {
            console.error('❌ [STORAGE] localStorage critically full, cannot save');
            this.notifyUser('Storage full. Please clear browser data.');
            return false;
          }
        }
      } else {
        console.error('❌ [STORAGE] Error saving to localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Safely get an item from localStorage
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if key not found
   * @returns {any} - Retrieved value or default
   */
  getItem(key, defaultValue = null) {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return defaultValue;
    }
    
    try {
      const item = localStorage.getItem(key);
      return item !== null ? item : defaultValue;
    } catch (error) {
      console.error('❌ [STORAGE] Error reading from localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Safely remove an item from localStorage
   * @param {string} key - Storage key
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('❌ [STORAGE] Error removing from localStorage:', error);
    }
  }

  /**
   * Safely get and parse JSON from localStorage
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if key not found or parse fails
   * @returns {any} - Parsed value or default
   */
  getJSON(key, defaultValue = null) {
    const item = this.getItem(key, null);
    if (item === null) return defaultValue;
    
    try {
      return JSON.parse(item);
    } catch (error) {
      console.error(`❌ [STORAGE] Error parsing JSON for key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Safely store JSON to localStorage
   * @param {string} key - Storage key
   * @param {any} value - Value to stringify and store
   * @returns {boolean} - Success status
   */
  setJSON(key, value) {
    try {
      const jsonString = JSON.stringify(value);
      return this.setItem(key, jsonString);
    } catch (error) {
      console.error(`❌ [STORAGE] Error stringifying JSON for key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clear log data from localStorage
   */
  clearLogs() {
    try {
      localStorage.removeItem('rainbowSails_logs');
      console.log('🧹 [STORAGE] Cleared logs');
    } catch (error) {
      console.error('❌ [STORAGE] Error clearing logs:', error);
    }
  }

  /**
   * Clear cached product data
   */
  clearCachedProducts() {
    try {
      localStorage.removeItem('gallery_products');
      localStorage.removeItem('products_cache');
      console.log('🧹 [STORAGE] Cleared cached products');
    } catch (error) {
      console.error('❌ [STORAGE] Error clearing products:', error);
    }
  }

  /**
   * Get current localStorage usage
   * @returns {Object} - Usage stats
   */
  getUsageStats() {
    let totalSize = 0;
    const items = {};

    try {
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const itemSize = (localStorage.getItem(key).length + key.length) * 2; // UTF-16 uses 2 bytes per char
          items[key] = itemSize;
          totalSize += itemSize;
        }
      }

      const maxSize = 5 * 1024 * 1024; // Approximate 5MB limit
      const usagePercent = (totalSize / maxSize) * 100;

      return {
        totalSize,
        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
        maxSizeMB: 5,
        usagePercent: usagePercent.toFixed(2),
        items,
        itemCount: Object.keys(items).length
      };
    } catch (error) {
      console.error('❌ [STORAGE] Error calculating usage:', error);
      return null;
    }
  }

  /**
   * Check if storage is getting full (> 80%)
   * @returns {boolean}
   */
  isStorageFull() {
    const stats = this.getUsageStats();
    return stats && stats.usagePercent > 80;
  }

  /**
   * Notify user about storage issues
   * @param {string} message
   */
  notifyUser(message) {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      console.warn(`⚠️ [STORAGE] ${message}`);
      
      // Could integrate with a toast notification system here
      // For now, just log to console
      
      // Example: If you have a toast library
      // if (window.toast) {
      //   window.toast.warning(message);
      // }
    }
  }

  /**
   * Perform preventive maintenance
   * Call this periodically to keep storage healthy
   */
  performMaintenance() {
    const stats = this.getUsageStats();
    
    if (stats && stats.usagePercent > 80) {
      console.warn(`⚠️ [STORAGE] Storage at ${stats.usagePercent}%, performing cleanup...`);
      
      // Clear logs if they're taking up space
      const logSize = stats.items['rainbowSails_logs'] || 0;
      if (logSize > 1024 * 1024) { // > 1MB
        this.clearLogs();
        console.log(`🧹 [STORAGE] Cleared ${(logSize / (1024 * 1024)).toFixed(2)}MB of logs`);
      }
    }
  }
}

// Export singleton instance
const safeStorage = new SafeStorage();
export default safeStorage;

